import { useAuthStore } from '@/store/auth.store'
import { useRoleStore } from '@/store/role.store'
import type { User } from '@/types/user.types'

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1'

const AUTH_EXEMPT_PATHS = new Set([
  '/auth/login',
  '/auth/register',
  '/auth/refresh',
  '/auth/logout',
])

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

interface ApiErrorBody {
  code?: string
  message?: string
  error?: string
}

let refreshPromise: Promise<User | null> | null = null

function normalizeError(status: number, body: Partial<ApiErrorBody>) {
  return new ApiError(
    status,
    body.code ?? 'UNKNOWN_ERROR',
    body.message ?? body.error ?? `HTTP ${status}`,
  )
}

async function parseErrorBody(response: Response) {
  return response.json().catch(() => ({} as Partial<ApiErrorBody>))
}

const CSRF_SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS'])

// readCookie pulls a cookie value from document.cookie. The identity service
// sets `csrf_token` as a non-HttpOnly cookie after every successful auth
// response (login / register / refresh), so we can read it here and
// double-submit it as a header on state-changing requests.
function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const prefix = `${name}=`
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim()
    if (trimmed.startsWith(prefix)) {
      return decodeURIComponent(trimmed.slice(prefix.length))
    }
  }
  return null
}

async function refreshSession() {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const response = await fetch(`${BASE_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      })

      if (!response.ok) {
        useAuthStore.getState().logout()
        useRoleStore.getState().syncRoles([])
        return null
      }

      const body = await response.json().catch(() => ({ user: null as User | null }))
      if (body?.user) {
        useAuthStore.getState().login(body.user)
        useRoleStore.getState().syncRoles(body.user.roles)
        return body.user
      }

      return null
    })().finally(() => {
      refreshPromise = null
    })
  }

  return refreshPromise
}

export async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
  allowRefresh = true,
): Promise<T> {
  const headers = new Headers(options.headers)
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData
  if (options.body !== undefined && !headers.has('Content-Type') && !isFormData) {
    headers.set('Content-Type', 'application/json')
  }

  // Double-submit CSRF token on state-changing requests. The cookie is set
  // by the server on every auth response; absence is fine on the very first
  // login/register call (those routes are CSRF-exempt server-side).
  const method = (options.method ?? 'GET').toUpperCase()
  if (!CSRF_SAFE_METHODS.has(method) && !headers.has('X-CSRF-Token')) {
    const csrfToken = readCookie('csrf_token')
    if (csrfToken) headers.set('X-CSRF-Token', csrfToken)
  }

  const response = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers,
    credentials: 'include',
  })

  if (response.status === 401 && allowRefresh && !AUTH_EXEMPT_PATHS.has(path)) {
    const user = await refreshSession()
    if (user) {
      return apiFetch<T>(path, options, false)
    }
  }

  if (!response.ok) {
    if (response.status === 401) {
      useAuthStore.getState().logout()
      useRoleStore.getState().syncRoles([])
    }
    throw normalizeError(response.status, await parseErrorBody(response))
  }

  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

export const api = {
  get: <T>(path: string, opts?: RequestInit) =>
    apiFetch<T>(path, { ...opts, method: 'GET' }),
  post: <T>(path: string, body?: unknown, opts?: RequestInit) =>
    apiFetch<T>(path, {
      ...opts,
      method: 'POST',
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  put: <T>(path: string, body: unknown, opts?: RequestInit) =>
    apiFetch<T>(path, { ...opts, method: 'PUT', body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown, opts?: RequestInit) =>
    apiFetch<T>(path, { ...opts, method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(path: string, opts?: RequestInit) =>
    apiFetch<T>(path, { ...opts, method: 'DELETE' }),
}
